/**
 * Safari-compatible date utilities
 * Safari has stricter requirements for date string formats compared to other browsers
 */

/**
 * Safely parse a date string that works across all browsers, including Safari
 * Treats values as date-only in LOCAL time (no UTC conversion)
 * @param dateString - Date string in various formats
 * @returns Date object or null if invalid
 */
export const safeDateParse = (dateString: string | Date | null | undefined): Date | null => {
    if (!dateString) return null;
    
    // If it's already a Date object, return it
    if (dateString instanceof Date) {
        return isNaN(dateString.getTime()) ? null : dateString;
    }
    
    // Convert to string if needed
    const str = String(dateString).trim();
    if (!str) return null;
    
    // Handle YYYY-MM-DD format FIRST (HTML date input/storage format)
    // This must come before ISO string check to avoid timezone conversion issues
    const yyyymmddMatch = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:T|$)/);
    if (yyyymmddMatch) {
        const [, year, month, day] = yyyymmddMatch;
        const date = new Date(Number(year), Number(month) - 1, Number(day)); // local
        if (!isNaN(date.getTime())) return date;
    }
    
    // Handle MM/DD/YYYY format (primary app format)
    const mmddyyyyMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyyMatch) {
        const [, month, day, year] = mmddyyyyMatch;
        const date = new Date(Number(year), Number(month) - 1, Number(day)); // local
        if (!isNaN(date.getTime())) return date;
    }
    
    // Try parsing as full ISO string (keep native behavior if time present)
    // This should come after date-only formats to prevent timezone conversion issues
    if (str.includes('T') || str.includes('Z')) {
        const isoDate = new Date(str);
        if (!isNaN(isoDate.getTime())) return isoDate;
    }
    
    // Note: We intentionally do not support DD/MM/YYYY to avoid ambiguity with MM/DD/YYYY
    
    // Try direct parsing as last resort
    const directDate = new Date(str);
    if (!isNaN(directDate.getTime())) return directDate;
    
    return null;
};

/**
 * Format date as MM/DD/YYYY for display
 * Uses UTC methods to prevent timezone issues in Safari
 * @param dateString - Date string or Date object
 * @returns Formatted date string or 'N/A' if invalid
 */
export const formatDateMMDDYYYY = (dateString: string | Date | null | undefined): string => {
    const date = safeDateParse(dateString);
    if (!date) return 'N/A';
    // Use local getters to keep date-only semantics
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

/**
 * Format date with time as MM/DD/YYYY HH:MM
 * @param dateString - Date string or Date object
 * @param timeString - Optional time string (HH:MM format)
 * @returns Formatted date-time string or 'N/A' if invalid
 */
export const formatDateWithTime = (dateString: string | Date | null | undefined, timeString?: string | null): string => {
    const date = safeDateParse(dateString);
    if (!date) return 'N/A';
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const dateStr = `${month}/${day}/${year}`;
    
    // Add time if available and not empty
    if (timeString && timeString.trim() !== '' && timeString !== '--:--') {
        const timeStr = timeString.slice(0, 5); // Convert "08:33:00" to "08:33"
        return `${dateStr} ${timeStr}`;
    }
    
    return dateStr;
};

/**
 * Format date with time in 12-hour format (MM/DD/YYYY HH:MM:SS AM/PM)
 * No timezone conversion - uses the date/time as-is from the string
 * @param dateString - Date string (ISO format or similar)
 * @returns Formatted date-time string in MM/DD/YYYY HH:MM:SS AM/PM format or 'N/A' if invalid
 */
export const formatDateWith12HourTime = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return 'N/A';
    
    let year: number, month: number, day: number, hour: number, minute: number, second: number;
    
    if (dateString instanceof Date) {
        year = dateString.getFullYear();
        month = dateString.getMonth() + 1;
        day = dateString.getDate();
        hour = dateString.getHours();
        minute = dateString.getMinutes();
        second = dateString.getSeconds();
    } else {
        // Parse the date string - if it's ISO format, extract components directly
        const str = String(dateString).trim();
        
        // Try to parse ISO format (YYYY-MM-DDTHH:mm:ss or YYYY-MM-DDTHH:mm:ss-HH:mm)
        const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
        if (isoMatch) {
            // Extract date and time components directly from ISO string (no timezone conversion)
            year = Number(isoMatch[1]);
            month = Number(isoMatch[2]);
            day = Number(isoMatch[3]);
            hour = Number(isoMatch[4]);
            minute = Number(isoMatch[5]);
            second = Number(isoMatch[6]);
        } else {
            // Fallback to safeDateParse
            const parsed = safeDateParse(dateString);
            if (!parsed) return 'N/A';
            year = parsed.getFullYear();
            month = parsed.getMonth() + 1;
            day = parsed.getDate();
            hour = parsed.getHours();
            minute = parsed.getMinutes();
            second = parsed.getSeconds();
        }
    }
    
    // Format date as MM/DD/YYYY
    const monthStr = String(month).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    
    // Format time in 12-hour format without seconds (HH:MM AM/PM)
    let hours12 = hour;
    const minutesStr = String(minute).padStart(2, '0');
    const ampm = hours12 >= 12 ? 'PM' : 'AM';
    hours12 = hours12 % 12;
    hours12 = hours12 ? hours12 : 12; // the hour '0' should be '12'
    const hoursStr = String(hours12).padStart(2, '0');
    
    return `${monthStr}/${dayStr}/${year} ${hoursStr}:${minutesStr} ${ampm}`;
};

/**
 * Format date and time separately in 12-hour format (MM/DD/YYYY HH:MM:SS AM/PM)
 * Handles cases where date and time come from separate fields
 * @param dateString - Date string (YYYY-MM-DD or ISO format)
 * @param timeString - Time string (HH:MM:SS or HH:MM format)
 * @returns Formatted date-time string in MM/DD/YYYY HH:MM:SS AM/PM format or 'N/A' if invalid
 */
export const formatDateTime12Hour = (dateString: string | null | undefined, timeString?: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    
    // Parse date
    const date = safeDateParse(dateString);
    if (!date) return 'N/A';
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const dateStr = `${month}/${day}/${year}`;
    
    // Parse time if provided
    let hour = 0, minute = 0, second = 0;
    
    if (timeString && timeString.trim() !== '') {
        if (/AM|PM/i.test(timeString)) {
            return `${dateStr} ${formatTime12Hour(timeString)}`;
        }

        // Handle HH:MM:SS or HH:MM format
        const timeParts = timeString.split(':');
        if (timeParts.length >= 2) {
            hour = Number(timeParts[0]) || 0;
            minute = Number(timeParts[1]) || 0;
            second = timeParts.length >= 3 ? (Number(timeParts[2]) || 0) : 0;
        }
    } else {
        // Use time from date object if timeString not provided
        hour = date.getHours();
        minute = date.getMinutes();
        second = date.getSeconds();
    }
    
    // Format time in 12-hour format without seconds (HH:MM AM/PM)
    let hours12 = hour;
    const minutesStr = String(minute).padStart(2, '0');
    const ampm = hours12 >= 12 ? 'PM' : 'AM';
    hours12 = hours12 % 12;
    hours12 = hours12 ? hours12 : 12; // the hour '0' should be '12'
    const hoursStr = String(hours12).padStart(2, '0');
    
    return `${dateStr} ${hoursStr}:${minutesStr} ${ampm}`;
};

/**
 * Format a time-only string (24-hour or 12-hour) for display as HH:MM AM/PM.
 * @param timeString - Time in HH:MM, HH:MM:SS, or HH:MM AM/PM format
 * @returns Formatted time or '--:--' when empty/invalid
 */
export const formatTime12Hour = (timeString: string | null | undefined): string => {
    if (!timeString || timeString.trim() === '' || timeString.trim() === '--:--') {
        return '--:--';
    }

    const trimmed = timeString.trim();

    const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (ampmMatch) {
        const hoursStr = String(Number(ampmMatch[1])).padStart(2, '0');
        return `${hoursStr}:${ampmMatch[2]} ${ampmMatch[4].toUpperCase()}`;
    }

    const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!timeMatch) return trimmed;

    let hour = Number(timeMatch[1]);
    const minute = timeMatch[2];
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    hour = hour ? hour : 12;
    const hoursStr = String(hour).padStart(2, '0');
    return `${hoursStr}:${minute} ${ampm}`;
};

/**
 * Format date for API payload (MM/DD/YYYY)
 * @param dateString - Date string or Date object
 * @returns Formatted date string or empty string if invalid
 */
export const formatDateForPayload = (dateString: string | Date | null | undefined): string => {
    const date = safeDateParse(dateString);
    if (!date) return '';
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${month}/${day}/${year}`;
};

/**
 * Format date for input field (YYYY-MM-DD)
 * Uses UTC methods to prevent timezone issues in Safari
 * @param dateString - Date string or Date object
 * @returns Formatted date string for HTML date input or empty string if invalid
 */
export const formatDateForInput = (dateString: string | Date | null | undefined): string => {
    let date: Date | null = null;
    
    // Handle Date objects directly to avoid timezone issues
    if (dateString instanceof Date) {
        date = dateString;
    } else {
        date = safeDateParse(dateString);
    }
    
    if (!date) return '';
    
    // Use local getters to keep date-only semantics
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Get today's date in YYYY-MM-DD format
 * @returns Today's date as ISO date string
 */
export const getTodayISO = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Format ReactDatePicker Date object for form input (YYYY-MM-DD)
 * This function handles Date objects directly to avoid timezone issues
 * Uses UTC methods to prevent timezone offset problems in Safari
 * @param date - Date object from ReactDatePicker
 * @returns Formatted date string for HTML date input or empty string if invalid
 */
export const formatDatePickerForInput = (date: Date | null): string => {
    if (!date) return '';
    // ReactDatePicker gives local Date; keep it local
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Validate if a date is valid and not in the past
 * @param dateString - Date string or Date object
 * @param allowToday - Whether to allow today's date (default: true)
 * @returns true if valid and not in the past
 */
export const validateDateNotPast = (dateString: string | Date | null | undefined, allowToday: boolean = true): boolean => {
    const date = safeDateParse(dateString);
    if (!date) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    
    return allowToday ? compareDate >= today : compareDate > today;
};

/**
 * Create a proper datetime for sorting
 * @param dateString - Date string or Date object
 * @param timeString - Optional time string (HH:MM format)
 * @returns Date object for sorting or epoch date for invalid dates
 */
export const getDateTimeForSorting = (dateString: string | Date | null | undefined, timeString?: string | null): Date => {
    const date = safeDateParse(dateString);
    if (!date) return new Date(0); // Return epoch for invalid dates
    
    // If we have a time and it's not empty or placeholder, combine it with the date
    if (timeString && timeString.trim() !== '' && timeString !== '--:--') {
        const [hours, minutes] = timeString.split(':').map(Number);
        if (!isNaN(hours) && !isNaN(minutes)) {
            const newDate = new Date(date);
            newDate.setHours(hours, minutes, 0, 0);
            return newDate;
        }
    }
    
    return date;
};

/**
 * Check if two dates are the same day
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if both dates are on the same day
 */
export const isSameDay = (date1: string | Date | null | undefined, date2: string | Date | null | undefined): boolean => {
    const d1 = safeDateParse(date1);
    const d2 = safeDateParse(date2);
    
    if (!d1 || !d2) return false;
    
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
};
